import { createContext, FC, useContext, useEffect, useState } from "react";

import { SelectChangeEvent } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getAllOrganizations,
  getAllPaises,
  getSublimationsByType,
} from "../../api/broker";
import { useAuthContext } from "../../hooks/Auth";
import { DropdownProps } from "../../interfaces/SuperAdmin/ContextProps/iEventos";
import { SublimacionContextProps } from "../../interfaces/SuperAdmin/ContextProps/iSublimacion";
import { iDataSublimacion } from "../../interfaces/SuperAdmin/Pages/iDataSublimacion";

const SublimacionContext = createContext<SublimacionContextProps>(
  {} as SublimacionContextProps
);

interface Props {
  children: React.ReactNode;
}

const SublimacionProvider: FC<Props> = ({ children }) => {
  const { orgOrPais, idPaisOrOrg } = useParams();
  const navigate = useNavigate();
  const { canAccess } = useAuthContext();

  const [tabValue, setTabValue] = useState(0);
  const [dropdownOptions, setDropdownOptions] = useState<DropdownProps[]>([]);
  const [isLoadingOpts, setIsLoadingOpts] = useState(false);
  const [isLoadingTable, setIsLoadingTable] = useState(false);

  const [selectedOpt, setSelectedOpt] = useState<string | undefined>(
    idPaisOrOrg
  );

  const [sublimaciones, setSublimaciones] = useState<iDataSublimacion[]>([]);

  // Cuando se cambia de url, se cambia el tab
  useEffect(() => {
    if (orgOrPais === "organizacion") setTabValue(0);
    else if (orgOrPais === "pais") setTabValue(1);
  }, [orgOrPais]);

  // Función para cambiar de Organización a Pais
  const handleTabChange = (newValue: 0 | 1) => {
    const url = newValue ? "pais" : "organizacion";
    navigate(`/asistente/auditorias/sublimacion/${url}`);
  };

  // Fetch de organizaciones
  const fetchOrgs = async () => {
    setIsLoadingOpts(true);
    try {
      const res = await getAllOrganizations();

      if (res.data.statusCode === 200) {
        const newArray = res.data.body.map((item) => {
          return {
            nombre: item.nombre,
            id: item.id_organizacion,
          };
        });
        setDropdownOptions(newArray);
      } else {
        toast.error("Error al obtener las organizaciones");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las organizaciones");
    } finally {
      setIsLoadingOpts(false);
    }
  };

  // Fetch de paises
  const fetchPaises = async () => {
    setIsLoadingOpts(true);
    try {
      const res = await getAllPaises();

      if (res.data.statusCode === 200) {
        const newArray = res.data.body.map((item) => {
          return {
            nombre: item.nombre,
            id: item.id_pais,
          };
        });
        setDropdownOptions(newArray);
      } else {
        toast.error("Error al obtener los paises");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los paises");
    } finally {
      setIsLoadingOpts(false);
    }
  };

  // Seleccionar una opción
  const handleSelectOpt = (event: SelectChangeEvent<unknown>) => {
    setSelectedOpt(event.target.value as string);
    const url = tabValue ? "pais" : "organizacion";
    navigate(
      `/asistente/auditorias/sublimacion/${url}/${event.target.value as string}`
    );
  };

  //Cada vez que se cambia el tab (Organizacion, Pais) hacemos el fetch de las opciones del primer select
  useEffect(() => {
    const isSuperAdmin = canAccess("view:reports");
    if (isSuperAdmin) {
      setSelectedOpt("");
      setSublimaciones([]);
      if (!tabValue) fetchOrgs();
      else if (tabValue === 1) fetchPaises();
    }
  }, [tabValue]);

  // Traer las auditorias en base a la opcion seleccionada y el tab
  const fetchAuditorias = async () => {
    setIsLoadingTable(true);
    try {
      // Creamos un objeto con los parámetros
      const params = {
        id_organizacion: !tabValue ? selectedOpt : undefined,
        id_pais: tabValue ? selectedOpt : undefined,
        id_usuario: undefined,
      };

      const res = await getSublimationsByType(
        params.id_organizacion,
        params.id_pais,
        params.id_usuario
      );

      console.log(res);
      if (res.status === 200) {
        const sortedData = res.data.body.sort(
          (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
        );
        setSublimaciones(sortedData);
      } else {
        toast.error("Error al obtener las sublimaciones");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener las sublimaciones");
    } finally {
      setIsLoadingTable(false);
    }
  };

  //Se hace el fetch de los eventos una vez seleccionada la opción
  useEffect(() => {
    if (selectedOpt) fetchAuditorias();
  }, [selectedOpt]);

  return (
    <SublimacionContext.Provider
      value={{
        tabValue,
        handleTabChange,
        isLoadingOpts,
        dropdownOptions,
        selectedOpt,
        handleSelectOpt,
        isLoadingTable,
        sublimaciones,
      }}
    >
      {children}
    </SublimacionContext.Provider>
  );
};

const useSuperAdminSublimacion = () => useContext(SublimacionContext);

export { SublimacionProvider, useSuperAdminSublimacion };
